import { memo, useCallback } from 'react';
import { View } from 'react-native';
import { datePickerHeaderItemStyles } from 'react-native-molecules/components/DatePicker';
import { useDatePickerInlineStoreValue } from 'react-native-molecules/components/DatePickerInline/store';
import { Icon, IconButton, Text, TouchableRipple } from '~/molecules';
import { StyleSheet } from 'react-native-unistyles';

type HeaderItemProps = {
    onNext?: (type: 'month' | 'year' | undefined) => void;
    onPressDropdown?: (type: 'month' | 'year') => void;
    onPrev?: (type: 'month' | 'year' | undefined) => void;
    pickerType: 'month' | 'year' | undefined;
    selecting?: boolean;
    type?: 'month' | 'year';
    value: number | string;
};

export const HeaderItem = memo((props: HeaderItemProps) => {
    const { onNext, onPrev, onPressDropdown, pickerType, selecting, type, value } = props;

    const { startDateYear, endDateYear } = useDatePickerInlineStoreValue((state) => ({
        startDateYear: state.startDateYear,
        endDateYear: state.endDateYear,
    }));

    const handlePressDropDown = useCallback(() => {
        if (type) {
            onPressDropdown?.(type);
        }
    }, [onPressDropdown, type]);

    const handleOnPrevious = useCallback(() => {
        onPrev?.(type);
    }, [onPrev, type]);

    const handleOnNext = useCallback(() => {
        onNext?.(type);
    }, [onNext, type]);

    const alignment = !onPressDropdown ? 'end' : !onNext ? 'start' : 'center';
    const disabled = pickerType && pickerType !== type;

    styles.useVariants({
        alignment,
        disabled,
        selectingOrDisabled: selecting || disabled,
    });

    return (
        <View style={[datePickerHeaderItemStyles.buttonContainer, styles.buttonContainer]} pointerEvents="box-none">
            {onPrev && (
                <View style={[datePickerHeaderItemStyles.buttonWrapper, styles.arrowContainer]}>
                    <IconButton
                        accessibilityLabel="Previous"
                        disabled={value === startDateYear || selecting || disabled}
                        name="chevron-left"
                        onPress={handleOnPrevious}
                        size={24}
                        style={styles.arrowButton}
                        type="material-community"
                    />
                </View>
            )}
            {type && (
                <TouchableRipple
                    accessibilityLabel={`${value}`}
                    accessibilityRole="button"
                    disabled={disabled}
                    onPress={handlePressDropDown}
                    style={datePickerHeaderItemStyles.buttonStyle}
                >
                    <View style={[datePickerHeaderItemStyles.innerStyle, styles.labelContainer]}>
                        <Text selectable={false} style={[datePickerHeaderItemStyles.labelStyle]}>
                            {value}
                        </Text>
                        <Icon
                            name={selecting && type === pickerType ? 'menu-up' : 'menu-down'}
                            onPress={handlePressDropDown}
                            size={18}
                            type="material-community"
                        />
                    </View>
                </TouchableRipple>
            )}
            {onNext && (
                <View style={[datePickerHeaderItemStyles.buttonWrapper, styles.arrowContainer]}>
                    <IconButton
                        accessibilityLabel="Next"
                        disabled={value === endDateYear || selecting || disabled}
                        name="chevron-right"
                        onPress={handleOnNext}
                        size={24}
                        style={styles.arrowButton}
                        type="material-community"
                    />
                </View>
            )}
        </View>
    );
});

const styles = StyleSheet.create(() => ({
    arrowContainer: {
        opacity: 1,
        variants: {
            selectingOrDisabled: {
                true: {
                    opacity: 0,
                },
            },
        },
    },
    arrowButton: {
        height: 28,
        width: 28,
    },
    buttonContainer: {
        variants: {
            alignment: {
                end: {
                    justifyContent: 'flex-end',
                },
                start: {
                    justifyContent: 'flex-start',
                },
                center: {
                    justifyContent: 'center',
                },
            },
        },
    },
    labelContainer: {
        variants: {
            disabled: {
                true: {
                    opacity: 0.5,
                    _web: {
                        cursor: 'default',
                    },
                },
            },
        },
    },
}));
